import { Link } from "react-router-dom";
import { Clock, Navigation, Phone } from "lucide-react";
import { CLINIC, NAV_LINKS } from "@/data/clinic";
import { GUIDES } from "@/data/guides";
import Logo from "./Logo";

const COMPANY = [
    { label: "About Us", to: "/about", id: "about" },
    { label: "FAQ", to: "/faq", id: "faq" },
    { label: "Contact", to: "/contact", id: "contact" },
];

const LEGAL = [
    { label: "Privacy Policy", to: "/privacy-policy" },
    { label: "Terms & Conditions", to: "/terms-and-conditions" },
    { label: "Medical Disclaimer", to: "/medical-disclaimer" },
];

export default function Footer() {
    return (
        <footer data-testid="site-footer" className="bg-ink text-cream">
            <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
                <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <Logo dark />
                        <p className="mt-5 text-sm leading-relaxed text-cream/70">
                            {CLINIC.addressLines.map((l, i) => (
                                <span key={i} className="block">
                                    {l}
                                </span>
                            ))}
                        </p>
                        <a
                            href={CLINIC.phoneTel}
                            data-testid="footer-phone"
                            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-gold transition-colors duration-300 hover:text-cream"
                        >
                            <Phone className="h-4 w-4" /> {CLINIC.phoneDisplay}
                        </a>
                        <p className="mt-3 inline-flex items-center gap-2 text-xs text-cream/60" data-testid="footer-hours">
                            <Clock className="h-3.5 w-3.5 text-gold" />
                            {CLINIC.hours.weekdays} • {CLINIC.hours.sunday}
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <a
                                href={CLINIC.phoneTel}
                                data-testid="footer-call-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-cream px-5 py-2.5 text-xs font-semibold text-ink transition-colors duration-300 hover:bg-gold"
                            >
                                <Phone className="h-3.5 w-3.5" /> Call Now
                            </a>
                            <a
                                href={CLINIC.mapsDirections}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="footer-directions-btn"
                                className="inline-flex items-center gap-2 rounded-full border border-cream/40 px-5 py-2.5 text-xs font-semibold text-cream transition-colors duration-300 hover:border-cream"
                            >
                                <Navigation className="h-3.5 w-3.5" /> Get Directions
                            </a>
                            <a
                                href={CLINIC.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="footer-book-btn"
                                className="inline-flex items-center gap-2 rounded-full border border-cream/40 px-5 py-2.5 text-xs font-semibold text-cream transition-colors duration-300 hover:border-cream"
                            >
                                Book a Consultation
                            </a>
                        </div>
                    </div>

                    <nav aria-label="Footer">
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-cream/50">Explore</p>
                        <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-1">
                            <li>
                                <Link to="/#home" data-testid="footer-link-home" className="text-sm text-cream/75 transition-colors duration-300 hover:text-gold">
                                    Home
                                </Link>
                            </li>
                            {NAV_LINKS.map((l) => (
                                <li key={l.id}>
                                    <Link
                                        to={`/#${l.id}`}
                                        data-testid={`footer-link-${l.id}`}
                                        className="text-sm text-cream/75 transition-colors duration-300 hover:text-gold"
                                    >
                                        {l.label === "Therapies" ? "Complementary Therapies" : l.label}
                                    </Link>
                                </li>
                            ))}
                            {COMPANY.map((l) => (
                                <li key={l.id}>
                                    <Link
                                        to={l.to}
                                        data-testid={`footer-link-${l.id}`}
                                        className="text-sm text-cream/75 transition-colors duration-300 hover:text-gold"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <nav aria-label="Footer guides">
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-cream/50">Learn</p>
                        <ul className="mt-5 space-y-3">
                            <li>
                                <Link
                                    to="/conditions-and-concerns"
                                    data-testid="footer-link-conditions"
                                    className="text-sm font-semibold text-cream/85 transition-colors duration-300 hover:text-gold"
                                >
                                    Conditions & Concerns
                                </Link>
                            </li>
                            {GUIDES.map((g) => (
                                <li key={g.slug}>
                                    <Link
                                        to={`/${g.slug}`}
                                        data-testid={`footer-link-${g.slug}`}
                                        className="text-sm text-cream/75 transition-colors duration-300 hover:text-gold"
                                    >
                                        {g.title.split(":")[0]}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-cream/50">Legal</p>
                        <ul className="mt-5 space-y-3">
                            {LEGAL.map((l) => (
                                <li key={l.to}>
                                    <Link
                                        to={l.to}
                                        data-testid={`footer-link-${l.to.slice(1)}`}
                                        className="text-sm text-cream/75 transition-colors duration-300 hover:text-gold"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="mt-14 border-t border-cream/15 pt-8">
                    <p className="text-xs leading-relaxed text-cream/50">
                        Agastya Chiro and Ancient Marma Therapy offers complementary wellness services
                        inspired by traditional practices. These services are not a substitute for medical
                        advice, diagnosis or treatment. Always consult a qualified physician for health
                        concerns.
                    </p>
                    <p className="mt-4 text-xs text-cream/40">
                        © {new Date().getFullYear()} {CLINIC.name}, Kurnool, Andhra Pradesh. All rights reserved.
                    </p>
                </div>
            </div>
        </footer>
    );
}
